// Client-side "Reset all" signal.
//
// The page-level Reset-all button is a Shiny actionButton. Its server handler
// snaps the Shiny-side sliders back to defaults, but it cannot reach two things:
//   - the React island inputs (bracket / curve editors own their own state,
//     not a Shiny widget), and
//   - the results column (the engine-output bridge only fires on Simulate).
// So each island and the results column listen for the button click here and
// reset themselves. Same client-side pattern as the Simulating signal — the
// click fires in the browser regardless of what the single-threaded R session
// is doing.

import { useEffect } from "react";

const RESET_ALL_BUTTON_SELECTOR = '[data-testid="reset-all-button"]';

/** Run `onReset` whenever the page-level "Reset all" button is clicked.
 *  Call once near a component that owns resettable state. Delegated on
 *  `document` so it survives Shiny re-rendering the button. */
export function useResetAllSignal(onReset: () => void): void {
  useEffect(() => {
    if (typeof document === "undefined") {
      return;
    }
    const handler = (event: MouseEvent): void => {
      const target = event.target as HTMLElement | null;
      if (target?.closest(RESET_ALL_BUTTON_SELECTOR)) {
        onReset();
      }
    };
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, [onReset]);
}
